import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useSupplierMitigationTasks } from '../../hooks/useSupplierMitigationTasks'
import { useSuppliers } from '../../hooks/useSuppliers'
import { exportToPdf, exportToWord } from '../../utils/exportCompanyMitigation'
import Modal from '../../components/ui/Modal'
import { RiskScaleLegend } from './RiskScaleLegend'
import {
  bandCountsUnique,
  collapsedBand,
  collapsedCategoryKey,
  collapsedMatchesLocation,
  collapsedSubCategoryKey,
  collapseSuppliersForPortfolio,
  collapseToBestSupplierRowsForPortfolio,
  numericScore,
  resolveCollapsedProfileSupplier,
  riskBand,
  rollupCollapsedByAttribute,
  rollupCollapsedByBrand,
  rollupCollapsedByLocation,
  supplierBand,
  supplierDisplayName,
  supplierMatchesLocation,
  type RollupRow,
} from './supplierRollup'
import type { Supplier, SupplierMitigationTask } from '../../types'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { EsBadge, EsButton, EsPageHeader, riskBadgeTone } from './ui'
import { CopyTableButtons } from './CopyTableButtons'
import { cell } from './copyTableToClipboard'
import {
  formatExclusivePercents,
  formatMetric,
  MetricModeToggle,
  type MetricMode,
} from './MetricModeToggle'
import { RiskBandChart, RollupBarsChart } from './RiskCharts'
import { DIMENSION_KEYS, scoreValue } from './supplierRiskRatingsTable'
import {
  matchesProcurementTypeFilter,
  type RiskFilter,
} from './esPortfolioFilters'
import { useEsPortfolioFilters } from './useEsPortfolioFilters'
import { buildPortfolioInsights } from './portfolioInsights'
import { ProcurementTypeSelect } from './ProcurementTypeSelect'
import { InsightStatements } from './InsightStatements'

const PAGE_SIZE = 25

function isOpenMitigation(t: SupplierMitigationTask): boolean {
  return t.status === 'open' || t.status === 'in_progress' || t.status === 'at_risk'
}

const MATRIX_HEADERS = [
  'Name',
  'Location',
  'Region',
  'Category',
  'Score',
  'Risk',
  'Financial',
  'Operational',
  'Legal',
  'Supply chain',
  'Outlook',
  'Top risk',
  'Next best action',
] as const

const ROLLUP_HEADERS_COUNT = ['Name', 'Count', 'High'] as const
const ROLLUP_HEADERS_PCT = ['Name', '%', 'High %'] as const

function supplierToMatrixRow(s: Supplier): string[] {
  return [
    cell(s.name),
    cell(s.location),
    cell(s.kenvue_region),
    cell(s.category || s.industry),
    s.overall_score != null ? s.overall_score.toFixed(1) : '—',
    cell(bandLabel(supplierBand(s))),
    cell(scoreValue(s, 'financial')),
    cell(scoreValue(s, 'operational')),
    cell(scoreValue(s, 'compliance')),
    cell(scoreValue(s, 'supply_chain')),
    cell(firstLine(s.outlook ?? s.risk_outlook, 200)),
    cell(firstLine(s.highest_risk_areas || s.key_takeaways, 200)),
    cell(firstLine(s.actions, 200)),
  ]
}

function bandLabel(band: 'low' | 'medium' | 'high' | null): string {
  if (band === 'high') return 'HIGH'
  if (band === 'medium') return 'MEDIUM'
  if (band === 'low') return 'LOW'
  return 'UNSCORED'
}

function splitLocationLabels(location: string | null | undefined): string[] {
  if (!location) return []
  return location
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && p !== 'Not Available')
}

function scoreTextClass(value: number | null): string {
  if (value == null) return 'text-es-muted'
  const b = riskBand(value)
  if (b === 'high') return 'font-semibold text-es-riskHigh'
  if (b === 'medium') return 'font-medium text-es-riskMedium'
  return 'text-es-riskLow'
}

function heatmapCellClass(value: number | null): string {
  if (value == null) return 'bg-es-haze text-es-muted'
  const b = riskBand(value)
  if (b === 'high') return 'bg-red-100 text-es-riskHigh'
  if (b === 'medium') return 'bg-amber-100 text-es-riskMedium'
  return 'bg-emerald-50 text-es-riskLow'
}

function firstLine(text: string | null | undefined, max = 120): string {
  if (!text) return '—'
  const line = text.split(/\n/).map((s) => s.trim()).filter(Boolean)[0] ?? text.trim()
  return line.length > max ? `${line.slice(0, max)}…` : line
}

function BulletList({ text }: { text: string }) {
  const lines = text
    .split(/\n/)
    .map((s) => s.replace(/^[\u2022\-\*]\s*/, '').trim())
    .filter(Boolean)
  if (lines.length === 0) return <span className="text-es-muted">—</span>
  return (
    <ul className="list-disc list-inside space-y-1 text-sm text-es-body">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  )
}

function NumberedActions({ text }: { text: string }) {
  const lines = text
    .split(/\n/)
    .map((s) => s.replace(/^\d+[\.\)]\s*/, '').replace(/^[\u2022\-\*]\s*/, '').trim())
    .filter(Boolean)
  if (lines.length === 0) return <span className="text-es-muted">—</span>
  return (
    <ol className="list-decimal list-inside space-y-1.5 text-sm text-es-body">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ol>
  )
}

function formatUpdated(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

type ProfileMode = 'overview' | 'detail'
type SortKey = 'name' | 'score' | 'region' | 'category'

const SCORE_HEADER_TITLE =
  'Master overall score (1–10). High above 6.0, Medium 4.1–6.0, Low 1.0–4.0'

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

function downloadMatrixCsv(headers: readonly string[], rows: string[][], filename: string): void {
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function supplierMatchesBrand(s: Supplier, brandFilter: string): boolean {
  if (!brandFilter) return true
  const brandsText = (s.brand || '').toLowerCase()
  return brandsText.split(',').some((part) => part.trim() === brandFilter.toLowerCase())
}

function collapsedMatchesBrand(
  s: { brands: string[] },
  brandFilter: string
): boolean {
  if (!brandFilter) return true
  const q = brandFilter.toLowerCase()
  return s.brands.some((b) => b.toLowerCase() === q)
}

function SupplierProfile({
  supplier,
  mode,
  onModeChange,
}: {
  supplier: Supplier
  mode: ProfileMode
  onModeChange: (m: ProfileMode) => void
}) {
  const band = supplierBand(supplier)
  const [expandedDim, setExpandedDim] = useState<string | null>(null)
  const title = supplierDisplayName(supplier)
  const parentLabel = (supplier.parent_name || '').trim()
  const showParent = !!parentLabel && parentLabel.toLowerCase() !== title.toLowerCase()
  const entityNames = (supplier.product_line_names || [])
    .map((n) => n.trim())
    .filter((n) => n && n.toLowerCase() !== title.toLowerCase())
  const entitiesLabel = entityNames.length ? entityNames.join(', ') : ''
  const meta = [
    supplier.location,
    supplier.kenvue_region,
    supplier.category || supplier.industry,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-bold text-es-navy">{title}</h3>
            <EsBadge tone={riskBadgeTone(band)} className="!text-xs !px-2 !py-0.5">
              {bandLabel(band)} RISK
            </EsBadge>
          </div>
          {meta && <p className="text-sm text-es-muted mt-1">{meta}</p>}
          <p className="text-xs text-es-muted mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {supplier.brand && <span>Brand: {supplier.brand}</span>}
            {supplier.procurement_type && (
              <span>
                {supplier.procurement_type === 'indirect' ? 'Indirect' : 'Direct'}
              </span>
            )}
            {showParent && <span>Parent: {parentLabel}</span>}
            {entitiesLabel && <span>Entities: {entitiesLabel}</span>}
            {supplier.spend_k != null && (
              <span>Spend ($K): {Number(supplier.spend_k).toLocaleString()}</span>
            )}
            {supplier.dm_em && <span>DM/EM: {supplier.dm_em}</span>}
            {supplier.single_source != null && (
              <span>{supplier.single_source ? 'Single Source' : 'Multi-source'}</span>
            )}
            {supplier.supplier_unique_id && <span>ID: {supplier.supplier_unique_id}</span>}
            <span>Updated {formatUpdated(supplier.updated_at)}</span>
          </p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold tabular-nums ${scoreTextClass(supplier.overall_score ?? null)}`}>
            {supplier.overall_score != null ? supplier.overall_score.toFixed(1) : '—'}
            <span className="text-base font-medium text-es-muted"> /10</span>
          </div>
          {supplier.risk_gauge && (
            <div className="text-xs text-es-muted mt-0.5">{supplier.risk_gauge}</div>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-es-soft pb-2">
        <button
          type="button"
          onClick={() => onModeChange('overview')}
          className={`px-3 py-1.5 text-sm font-semibold rounded-[var(--radius-sm)] border ${
            mode === 'overview'
              ? 'bg-es-signal text-white border-es-signal'
              : 'bg-es-white text-es-body border-es-hairline hover:bg-es-haze'
          }`}
        >
          Overview
        </button>
        <button
          type="button"
          onClick={() => onModeChange('detail')}
          className={`px-3 py-1.5 text-sm font-semibold rounded-[var(--radius-sm)] border ${
            mode === 'detail'
              ? 'bg-es-signal text-white border-es-signal'
              : 'bg-es-white text-es-body border-es-hairline hover:bg-es-haze'
          }`}
        >
          Risk detail
        </button>
      </div>

      {mode === 'overview' ? (
        <>
          {(supplier.highest_risk_areas || supplier.key_takeaways || supplier.outlook) && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">
                Executive Risk Summary
              </h4>
              <BulletList
                text={
                  supplier.highest_risk_areas ||
                  supplier.key_takeaways ||
                  supplier.outlook ||
                  ''
                }
              />
            </section>
          )}

          <section>
            <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">
              Risk Heatmap (14 Categories)
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {DIMENSION_KEYS.map(({ key, label }) => {
                const v = scoreValue(supplier, key)
                return (
                  <div
                    key={key}
                    className={`rounded-[var(--radius-md)] border border-es-soft p-2 text-center ${heatmapCellClass(v)}`}
                  >
                    <div className="text-lg font-bold tabular-nums">{v != null ? v : '–'}</div>
                    <div className="text-[10px] font-medium leading-tight mt-0.5 opacity-80">{label}</div>
                  </div>
                )
              })}
            </div>
          </section>

          {supplier.risk_outlook && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">Risk Outlook</h4>
              <p className="text-sm text-es-body whitespace-pre-wrap">{supplier.risk_outlook}</p>
            </section>
          )}

          {supplier.actions && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">
                Next best actions
              </h4>
              <NumberedActions text={supplier.actions} />
            </section>
          )}
        </>
      ) : (
        <>
          <section>
            <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">
              Risk Ratings by Category
            </h4>
            <ul className="divide-y divide-es-soft border border-es-soft rounded-[var(--radius-md)] overflow-hidden">
              {DIMENSION_KEYS.map(({ key, label }) => {
                const v = scoreValue(supplier, key)
                const narrative = supplier.risk_dimension_narratives?.[key]
                const open = expandedDim === key
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-es-haze"
                      onClick={() => setExpandedDim(open ? null : key)}
                      disabled={!narrative}
                    >
                      <span className="text-sm text-es-body">{label}</span>
                      <span className={`text-sm tabular-nums ${scoreTextClass(v)}`}>
                        {v != null ? `${v}/10` : '–'}
                        {narrative && (
                          <i
                            className={`fa-solid fa-chevron-${open ? 'up' : 'down'} ml-2 text-es-muted text-xs`}
                            aria-hidden
                          />
                        )}
                      </span>
                    </button>
                    {open && narrative && (
                      <div className="px-3 pb-3 text-sm text-es-body whitespace-pre-wrap border-t border-es-soft bg-es-haze/50">
                        {narrative}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>

          {supplier.risk_outlook && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">Outlook</h4>
              <p className="text-sm text-es-body whitespace-pre-wrap">{supplier.risk_outlook}</p>
            </section>
          )}

          {supplier.questions_to_ask && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">
                Questions to Ask Supplier
              </h4>
              <BulletList text={supplier.questions_to_ask} />
            </section>
          )}

          {supplier.actions && (
            <section>
              <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-2">Next best action</h4>
              <p className="text-sm text-es-body whitespace-pre-wrap">{supplier.actions}</p>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function CompareCard({ supplier }: { supplier: Supplier }) {
  const band = supplierBand(supplier)
  return (
    <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 min-w-[220px] flex-1">
      <div className="font-semibold text-es-navy">{supplierDisplayName(supplier)}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${scoreTextClass(supplier.overall_score ?? null)}`}>
        {supplier.overall_score != null ? supplier.overall_score.toFixed(1) : '—'}
      </div>
      <p className="text-xs text-es-muted mt-1">
        {[supplier.location, supplier.kenvue_region, supplier.category].filter(Boolean).join(' · ')}
      </p>
      <EsBadge tone={riskBadgeTone(band)} className="!inline-block !mt-2 !text-xs !px-2 !py-0.5">
        {bandLabel(band)}
      </EsBadge>
      <ul className="mt-3 space-y-1 text-xs">
        {DIMENSION_KEYS.map(({ key, label }) => (
          <li key={key} className="flex justify-between gap-2">
            <span className="text-es-muted truncate">{label}</span>
            <span className={`tabular-nums ${scoreTextClass(scoreValue(supplier, key))}`}>
              {scoreValue(supplier, key) ?? '–'}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-es-body whitespace-pre-wrap">
        <span className="font-semibold text-es-muted">Outlook:</span>{' '}
        {(supplier.risk_outlook || supplier.outlook || '—').trim() || '—'}
      </p>
      <p className="mt-1 text-xs text-es-body whitespace-pre-wrap">
        <span className="font-semibold text-es-muted">Action:</span>{' '}
        {(supplier.actions || '—').trim() || '—'}
      </p>
    </div>
  )
}

export function RiskMatrixView() {
  const { isAdmin } = useAuth()
  const { suppliers, loading, error } = useSuppliers(isAdmin ? { scope: 'all' } : undefined)
  const { tasks: mitigationTasks } = useSupplierMitigationTasks()
  const {
    filters,
    setRisk,
    setCategory,
    setSubCategory,
    setBrand,
    setRegion,
    setLocation,
    setSingleSource,
    setProcurementType,
    clearAll,
    supplierParam,
    setSupplierParam,
  } = useEsPortfolioFilters()

  const riskFilter = filters.risk
  const regionFilter = filters.region
  const categoryFilter = filters.category
  const subCategoryFilter = filters.subCategory
  const countryFilter = filters.location
  const brandFilter = filters.brand
  const singleSourceFilter = filters.singleSource
  const procurementTypeFilter = filters.procurementType

  const [search, setSearch] = useState('')
  const [metricMode, setMetricMode] = useState<MetricMode>('count')
  const [sortKey, setSortKey] = useState<SortKey>('score')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [selected, setSelected] = useState<Supplier | null>(null)
  const [profileMode, setProfileMode] = useState<ProfileMode>('overview')
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [compareOpen, setCompareOpen] = useState(false)
  const [addCompareOpen, setAddCompareOpen] = useState(false)

  const scopedSuppliers = useMemo(
    () =>
      suppliers.filter((s) =>
        matchesProcurementTypeFilter(s.procurement_type, procurementTypeFilter)
      ),
    [suppliers, procurementTypeFilter]
  )

  const base = useMemo(
    () => collapseToBestSupplierRowsForPortfolio(scopedSuppliers, procurementTypeFilter),
    [scopedSuppliers, procurementTypeFilter]
  )
  const collapsedAll = useMemo(
    () => collapseSuppliersForPortfolio(scopedSuppliers, procurementTypeFilter),
    [scopedSuppliers, procurementTypeFilter]
  )

  // Deep-link: ?supplier=<id> opens profile via collapsed grain (never raw child alone)
  useEffect(() => {
    if (!supplierParam || suppliers.length === 0) return
    const match = resolveCollapsedProfileSupplier(
      supplierParam,
      base,
      scopedSuppliers,
      procurementTypeFilter
    )
    if (match) {
      setSelected(match)
      setProfileMode('overview')
    }
  }, [supplierParam, suppliers, base, scopedSuppliers, procurementTypeFilter])

  const regions = useMemo(
    () =>
      [...new Set(base.map((s) => s.kenvue_region).filter((x): x is string => !!x && x !== 'Not Available'))].sort(),
    [base]
  )
  const categories = useMemo(
    () =>
      [...new Set(base.map((s) => s.category || s.industry).filter((x): x is string => !!x))].sort(),
    [base]
  )

  /** Cascade: sub-category options from suppliers matching parent filters (except sub itself). */
  const subCategories = useMemo(() => {
    const list = base.filter((s) => {
      const band = supplierBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (regionFilter && s.kenvue_region !== regionFilter) return false
      if (categoryFilter && (s.category || s.industry) !== categoryFilter) return false
      if (!supplierMatchesLocation(s, countryFilter)) return false
      if (!supplierMatchesBrand(s, brandFilter)) return false
      return true
    })
    return [
      ...new Set(list.map((s) => s.sub_category).filter((x): x is string => !!x && x !== 'Not Available')),
    ].sort((a, b) => a.localeCompare(b))
  }, [base, riskFilter, regionFilter, categoryFilter, countryFilter, brandFilter])

  /** Cascade: countries from suppliers in selected region (+ other parent filters except country). */
  const countries = useMemo(() => {
    const list = base.filter((s) => {
      const band = supplierBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (regionFilter && s.kenvue_region !== regionFilter) return false
      if (categoryFilter && (s.category || s.industry) !== categoryFilter) return false
      if (subCategoryFilter && (s.sub_category || '') !== subCategoryFilter) return false
      if (!supplierMatchesBrand(s, brandFilter)) return false
      return true
    })
    const set = new Set<string>()
    for (const s of list) {
      for (const loc of splitLocationLabels(s.location)) set.add(loc)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [base, riskFilter, regionFilter, categoryFilter, subCategoryFilter, brandFilter])

  const brands = useMemo(() => {
    const set = new Set<string>()
    for (const s of collapsedAll) {
      for (const b of s.brands) {
        if (b && b !== 'Not Available' && b !== 'NA') set.add(b)
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [collapsedAll])

  useEffect(() => {
    if (subCategoryFilter && !subCategories.includes(subCategoryFilter)) {
      setSubCategory('')
    }
  }, [subCategories, subCategoryFilter])

  useEffect(() => {
    if (countryFilter && !countries.includes(countryFilter)) {
      setLocation('')
    }
  }, [countries, countryFilter])

  /** KPIs / rollups / charts follow dimension filters (not free-text search). */
  const collapsedForMetrics = useMemo(() => {
    return collapsedAll.filter((s) => {
      const band = collapsedBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (regionFilter && (s.kenvue_region || '') !== regionFilter) return false
      if (categoryFilter && (s.category || '') !== categoryFilter) return false
      if (subCategoryFilter && (s.sub_category || '') !== subCategoryFilter) return false
      if (!collapsedMatchesLocation(s, countryFilter)) return false
      if (!collapsedMatchesBrand(s, brandFilter)) return false
      if (singleSourceFilter === 'yes' && s.single_source !== true) return false
      if (singleSourceFilter === 'no' && s.single_source !== false) return false
      return true
    })
  }, [
    collapsedAll,
    riskFilter,
    regionFilter,
    categoryFilter,
    subCategoryFilter,
    countryFilter,
    brandFilter,
    singleSourceFilter,
  ])

  const kpis = useMemo(() => bandCountsUnique(collapsedForMetrics), [collapsedForMetrics])

  const dimensionRollups = useMemo(() => {
    const byKeyAz = (rows: RollupRow[]) => [...rows].sort((a, b) => a.key.localeCompare(b.key))
    return {
      byBrand: byKeyAz(rollupCollapsedByBrand(collapsedForMetrics)),
      byCategory: byKeyAz(rollupCollapsedByAttribute(collapsedForMetrics, collapsedCategoryKey)),
      byCountry: byKeyAz(rollupCollapsedByLocation(collapsedForMetrics)),
      bySubCategory: byKeyAz(
        rollupCollapsedByAttribute(collapsedForMetrics, collapsedSubCategoryKey)
      ),
    }
  }, [collapsedForMetrics])

  const insights = useMemo(() => {
    const openBySupplierId = new Map<string, number>()
    for (const t of mitigationTasks) {
      if (!t.supplier_id || !isOpenMitigation(t)) continue
      openBySupplierId.set(t.supplier_id, (openBySupplierId.get(t.supplier_id) || 0) + 1)
    }
    const highRisk = collapsedForMetrics.filter((s) => collapsedBand(s) === 'high')
    let highWithOpenMitigation = 0
    for (const s of highRisk) {
      if (s.productLineIds.some((id) => (openBySupplierId.get(id) || 0) > 0)) {
        highWithOpenMitigation++
      }
    }
    return buildPortfolioInsights({
      collapsed: collapsedForMetrics,
      highWithOpenMitigation,
      emphasizeCountry: true,
    })
  }, [collapsedForMetrics, mitigationTasks])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = base.filter((s) => {
      if (q) {
        const displayHit = supplierDisplayName(s).toLowerCase().includes(q)
        const nameHit = (s.name ?? '').toLowerCase().includes(q)
        const parentHit = (s.parent_name ?? '').toLowerCase().includes(q)
        const idHit = (s.supplier_unique_id ?? '').toLowerCase().includes(q)
        const lineHit = (s.product_line_names || []).some((n) => n.toLowerCase().includes(q))
        if (!displayHit && !nameHit && !parentHit && !idHit && !lineHit) return false
      }
      const band = supplierBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (regionFilter && s.kenvue_region !== regionFilter) return false
      if (categoryFilter && (s.category || s.industry) !== categoryFilter) return false
      if (subCategoryFilter && (s.sub_category || '') !== subCategoryFilter) return false
      if (!supplierMatchesLocation(s, countryFilter)) return false
      if (!supplierMatchesBrand(s, brandFilter)) return false
      if (singleSourceFilter === 'yes' && s.single_source !== true) return false
      if (singleSourceFilter === 'no' && s.single_source !== false) return false
      return true
    })
    list = [...list].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'name') cmp = supplierDisplayName(a).localeCompare(supplierDisplayName(b))
      else if (sortKey === 'score')
        cmp = (numericScore(a.overall_score) ?? -1) - (numericScore(b.overall_score) ?? -1)
      else if (sortKey === 'region') cmp = (a.kenvue_region ?? '').localeCompare(b.kenvue_region ?? '')
      else if (sortKey === 'category')
        cmp = (a.category || a.industry || '').localeCompare(b.category || b.industry || '')
      return sortDir === 'asc' ? cmp : -cmp
    })
    return list
  }, [
    base,
    search,
    riskFilter,
    regionFilter,
    categoryFilter,
    subCategoryFilter,
    countryFilter,
    brandFilter,
    singleSourceFilter,
    sortKey,
    sortDir,
  ])

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = []
    if (search.trim()) {
      chips.push({ key: 'search', label: `Search: ${search.trim()}`, clear: () => setSearch('') })
    }
    if (riskFilter !== 'all') {
      chips.push({
        key: 'risk',
        label: `Risk: ${riskFilter}`,
        clear: () => setRisk('all'),
      })
    }
    if (regionFilter) {
      chips.push({ key: 'region', label: `Region: ${regionFilter}`, clear: () => setRegion('') })
    }
    if (categoryFilter) {
      chips.push({
        key: 'category',
        label: `Category: ${categoryFilter}`,
        clear: () => setCategory(''),
      })
    }
    if (subCategoryFilter) {
      chips.push({
        key: 'sub',
        label: `Sub-category: ${subCategoryFilter}`,
        clear: () => setSubCategory(''),
      })
    }
    if (countryFilter) {
      chips.push({
        key: 'country',
        label: `Country: ${countryFilter}`,
        clear: () => setLocation(''),
      })
    }
    if (brandFilter) {
      chips.push({ key: 'brand', label: `Brand: ${brandFilter}`, clear: () => setBrand('') })
    }
    if (singleSourceFilter !== 'all') {
      chips.push({
        key: 'ss',
        label: `Single-source: ${singleSourceFilter}`,
        clear: () => setSingleSource('all'),
      })
    }
    if (procurementTypeFilter !== 'direct') {
      chips.push({
        key: 'pt',
        label:
          procurementTypeFilter === 'all'
            ? 'Procurement: All'
            : `Procurement: Indirect`,
        clear: () => setProcurementType('direct'),
      })
    }
    return chips
  }, [
    search,
    riskFilter,
    regionFilter,
    categoryFilter,
    subCategoryFilter,
    countryFilter,
    brandFilter,
    singleSourceFilter,
    procurementTypeFilter,
    setRisk,
    setRegion,
    setCategory,
    setSubCategory,
    setLocation,
    setBrand,
    setSingleSource,
    setProcurementType,
  ])

  const clearAllFilters = () => {
    setSearch('')
    clearAll()
  }

  useEffect(() => {
    setPage(1)
  }, [
    search,
    riskFilter,
    regionFilter,
    categoryFilter,
    subCategoryFilter,
    countryFilter,
    brandFilter,
    singleSourceFilter,
    procurementTypeFilter,
  ])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const matrixRowsAll = useMemo(() => filtered.map(supplierToMatrixRow), [filtered])

  const compareSuppliers = useMemo(
    () => compareIds.map((id) => base.find((s) => s.id === id)).filter((s): s is Supplier => !!s),
    [compareIds, base]
  )

  const toggleCompare = (id: string) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 4) return prev
      return [...prev, id]
    })
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  useEffect(() => {
    if (error) console.error('Suppliers load error:', error)
  }, [error])

  const selectClass =
    'rounded-[var(--radius-sm)] border border-es-hairline px-2.5 py-2 text-sm bg-es-white text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal'

  return (
    <div className="p-4 md:p-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm mb-4" role="alert">
          {toErrorMessage(error, 'Failed to load suppliers.')}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <EsPageHeader
          className="mb-0"
          title="Intelligence view"
          description="Detailed exploration: filter, compare, and open supplier profiles. Executive insights live on Risk overview; open mitigations are tracked on Risk & mitigation monitor."
          eyebrow="Risk matrix"
        />
        <div className="flex flex-wrap gap-2">
          {compareIds.length > 0 && (
            <EsButton type="button" variant="secondary" onClick={() => setCompareOpen(true)} className="!rounded-full">
              Compare ({compareIds.length})
            </EsButton>
          )}
          {selected && (
            <>
              <EsButton type="button" variant="secondary" onClick={() => exportToPdf(selected, [], [])} className="!px-3 !py-2 !text-sm">
                Export PDF
              </EsButton>
              <EsButton type="button" variant="secondary" onClick={() => exportToWord(selected, [], [])} className="!px-3 !py-2 !text-sm">
                Export Word
              </EsButton>
            </>
          )}
        </div>
      </div>
      <ProcurementTypeSelect
        value={procurementTypeFilter}
        onChange={setProcurementType}
        className="mb-4"
      />
      <RiskScaleLegend />

      <div className="mb-4">
        <InsightStatements insights={insights} title="Key insights" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs text-es-muted">
          Supporting metrics ·{' '}
          {metricMode === 'pct'
            ? '% of filtered set (band KPIs sum to 100%; High % within group)'
            : 'counts'}
        </p>
        <MetricModeToggle value={metricMode} onChange={setMetricMode} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {(() => {
          const bandPct =
            metricMode === 'pct'
              ? formatExclusivePercents([kpis.high, kpis.medium, kpis.low, kpis.unscored])
              : null
          return (
            [
              {
                label: 'Suppliers',
                value: formatMetric(kpis.total, kpis.total, metricMode),
                className: 'text-es-navy',
                filter: 'all' as RiskFilter,
              },
              {
                label: 'High risk',
                value: bandPct ? bandPct[0] : String(kpis.high),
                className: 'text-es-riskHigh',
                filter: 'high' as RiskFilter,
              },
              {
                label: 'Medium risk',
                value: bandPct ? bandPct[1] : String(kpis.medium),
                className: 'text-es-riskMedium',
                filter: 'medium' as RiskFilter,
              },
              {
                label: 'Low risk',
                value: bandPct ? bandPct[2] : String(kpis.low),
                className: 'text-es-riskLow',
                filter: 'low' as RiskFilter,
              },
              {
                label: 'Unscored',
                value: bandPct ? bandPct[3] : String(kpis.unscored),
                className: 'text-es-muted',
                filter: 'unscored' as RiskFilter,
              },
            ] as const
          )
        })().map((k) => {
          const active = riskFilter === k.filter
          return (
            <button
              key={k.label}
              type="button"
              onClick={() => setRisk(k.filter)}
              aria-pressed={active}
              className={`rounded-[var(--radius-lg)] px-4 py-3 shadow-[var(--shadow-card)] border text-left transition-colors bg-es-white ${
                active
                  ? 'border-es-signal ring-1 ring-es-signal'
                  : 'border-es-soft hover:border-es-signal hover:bg-es-haze/60'
              }`}
            >
              <div className={`text-2xl font-bold tabular-nums ${k.className}`}>{k.value}</div>
              <div className="text-xs font-semibold text-es-muted uppercase tracking-wider mt-0.5">
                {k.label}
              </div>
            </button>
          )
        })}
      </div>
      <p className="text-xs text-es-muted -mt-2 mb-4">
        High + Medium + Low + Unscored = {kpis.high + kpis.medium + kpis.low + kpis.unscored} (matches{' '}
        {kpis.total} unique suppliers by highest score). Table lists one row per supplier (brands
        combined); By brand counts brand impacts. KPIs follow dimension filters (search only
        affects the table).
      </p>
      {procurementTypeFilter === 'indirect' && kpis.unscored > 0 && (
        <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2 mb-4">
          {kpis.unscored} unique Indirect supplier{kpis.unscored === 1 ? '' : 's'} have no risk
          scores (master rows kept from import — expected on the Indirect workbook).
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <RiskBandChart
          bands={{
            high: kpis.high,
            medium: kpis.medium,
            low: kpis.low,
            unscored: kpis.unscored,
            total: kpis.total,
          }}
          title="Risk distribution"
          onBandClick={(band) => setRisk(band)}
        />
        <RollupBarsChart
          title="Top categories by high risk"
          rows={dimensionRollups.byCategory}
          valueKey="high"
          portfolioTotal={kpis.total}
          onRowClick={(key) => setCategory(key)}
        />
        <RollupBarsChart
          title="Top countries by high risk"
          rows={dimensionRollups.byCountry}
          valueKey="high"
          portfolioTotal={kpis.total}
          onRowClick={(key) => setLocation(key)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <CompactRollup
          title="By brand"
          rows={dimensionRollups.byBrand}
          alphabetical
          metricMode={metricMode}
          denom={dimensionRollups.byBrand.reduce((n, r) => n + r.count, 0) || 1}
          onRowClick={(key) => setBrand(key === 'Unclassified' ? '' : key)}
        />
        <CompactRollup
          title="By category"
          rows={dimensionRollups.byCategory}
          alphabetical
          metricMode={metricMode}
          denom={kpis.total}
          onRowClick={(key) => setCategory(key === 'Unclassified' ? '' : key)}
        />
        <CompactRollup
          title="By country"
          rows={dimensionRollups.byCountry}
          alphabetical
          metricMode={metricMode}
          denom={kpis.total}
          onRowClick={(key) => setLocation(key === 'Unclassified' ? '' : key)}
        />
        <CompactRollup
          title="By sub-category"
          rows={dimensionRollups.bySubCategory}
          alphabetical
          metricMode={metricMode}
          denom={kpis.total}
          onRowClick={(key) => setSubCategory(key === 'Unclassified' ? '' : key)}
        />
      </div>

      <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] overflow-hidden">
        <div className="flex flex-wrap gap-2 p-3 border-b border-es-soft bg-es-haze/80">
          <input
            type="search"
            placeholder="Search name or supplier ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${selectClass} min-w-[180px] flex-1`}
            aria-label="Search name or supplier ID"
          />
          <select
            value={riskFilter}
            onChange={(e) => setRisk(e.target.value as RiskFilter)}
            className={selectClass}
            aria-label="Risk level"
          >
            <option value="all">Risk Level</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="unscored">Unscored</option>
          </select>
          <select
            value={regionFilter}
            onChange={(e) => setRegion(e.target.value)}
            className={selectClass}
            aria-label="Region"
          >
            <option value="">Region</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategory(e.target.value)}
            className={selectClass}
            aria-label="Category"
          >
            <option value="">Category</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={subCategoryFilter}
            onChange={(e) => setSubCategory(e.target.value)}
            className={selectClass}
            aria-label="Sub-category"
          >
            <option value="">Sub-category</option>
            {subCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={countryFilter}
            onChange={(e) => setLocation(e.target.value)}
            className={selectClass}
            aria-label="Country"
          >
            <option value="">Country</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={brandFilter}
            onChange={(e) => setBrand(e.target.value)}
            className={selectClass}
            aria-label="Brand"
          >
            <option value="">Brand</option>
            {brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-es-soft bg-es-white">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border border-es-hairline bg-es-haze text-es-body hover:border-es-signal"
                title={`Clear ${chip.label}`}
              >
                {chip.label}
                <i className="fa-solid fa-xmark text-[10px] text-es-muted" aria-hidden />
              </button>
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs font-semibold text-es-signal hover:text-es-deep hover:underline ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-es-muted">
            <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-es-muted">No suppliers match your filters.</div>
        ) : (
          <>
            <div className="px-3 py-2 border-b border-es-soft flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] text-es-muted">
                Copy / Export = all filtered rows
              </p>
              <div className="inline-flex flex-wrap items-center gap-1.5">
                <CopyTableButtons
                  payload={{ headers: [...MATRIX_HEADERS], rows: matrixRowsAll }}
                />
                <button
                  type="button"
                  disabled={matrixRowsAll.length === 0}
                  title="Export filtered rows as CSV"
                  onClick={() =>
                    downloadMatrixCsv(
                      MATRIX_HEADERS,
                      matrixRowsAll,
                      `intelligence-view-${new Date().toISOString().slice(0, 10)}.csv`
                    )
                  }
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider rounded border transition-colors text-es-muted border-es-soft hover:border-es-signal hover:text-es-signal bg-es-white disabled:opacity-50"
                >
                  <i className="fa-solid fa-file-csv" aria-hidden />
                  Export CSV
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-es-haze border-b border-es-soft text-xs font-bold text-es-muted uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-3 text-left w-8">Cmp</th>
                    <th className="px-3 py-3 text-left cursor-pointer" onClick={() => toggleSort('name')}>
                      Supplier {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="px-3 py-3 text-left">Location</th>
                    <th className="px-3 py-3 text-left cursor-pointer" onClick={() => toggleSort('region')}>
                      Region
                    </th>
                    <th className="px-3 py-3 text-left cursor-pointer" onClick={() => toggleSort('category')}>
                      Category
                    </th>
                    <th
                      className="px-3 py-3 text-right cursor-pointer"
                      onClick={() => toggleSort('score')}
                      title={SCORE_HEADER_TITLE}
                    >
                      Score
                    </th>
                    <th className="px-3 py-3 text-left">Risk</th>
                    <th className="px-3 py-3 text-right" title="Financial">
                      Fin
                    </th>
                    <th className="px-3 py-3 text-right" title="Operational">
                      Ops
                    </th>
                    <th className="px-3 py-3 text-right" title="Legal">
                      Legal
                    </th>
                    <th className="px-3 py-3 text-right" title="Supply chain">
                      SC
                    </th>
                    <th className="px-3 py-3 text-left min-w-[140px]">Outlook</th>
                    <th className="px-3 py-3 text-left min-w-[140px]">Top risk</th>
                    <th className="px-3 py-3 text-left min-w-[140px]">Next best action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-es-soft">
                  {pageRows.map((s) => {
                    const band = supplierBand(s)
                    return (
                      <tr key={s.id} className="hover:bg-es-haze">
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={compareIds.includes(s.id)}
                            onChange={() => toggleCompare(s.id)}
                            aria-label={`Compare ${supplierDisplayName(s)}`}
                            className="rounded border-es-hairline text-es-signal focus:ring-es-signal"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            type="button"
                            onClick={() => {
                              const resolved = resolveCollapsedProfileSupplier(
                                s.id,
                                base,
                                scopedSuppliers,
                                procurementTypeFilter
                              )
                              if (resolved) setSelected(resolved)
                              setProfileMode('overview')
                              setSupplierParam(s.id)
                            }}
                            className="font-semibold text-es-signal hover:text-es-deep underline-offset-2 hover:underline text-left"
                          >
                            {supplierDisplayName(s)}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-es-body whitespace-nowrap">{s.location ?? '—'}</td>
                        <td className="px-3 py-2.5 text-es-body whitespace-nowrap">
                          {s.kenvue_region ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-es-body whitespace-nowrap">
                          {s.category || s.industry || '—'}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${scoreTextClass(s.overall_score ?? null)}`}>
                          {s.overall_score != null ? s.overall_score.toFixed(1) : '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <EsBadge tone={riskBadgeTone(band)} className="!text-xs !px-1.5 !py-0.5">
                            {bandLabel(band)}
                          </EsBadge>
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${scoreTextClass(scoreValue(s, 'financial'))}`}>
                          {scoreValue(s, 'financial') ?? '—'}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${scoreTextClass(scoreValue(s, 'operational'))}`}>
                          {scoreValue(s, 'operational') ?? '—'}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${scoreTextClass(scoreValue(s, 'compliance'))}`}>
                          {scoreValue(s, 'compliance') ?? '—'}
                        </td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${scoreTextClass(scoreValue(s, 'supply_chain'))}`}>
                          {scoreValue(s, 'supply_chain') ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-es-body text-xs">
                          {firstLine(s.risk_outlook || s.outlook, 60)}
                        </td>
                        <td className="px-3 py-2.5 text-es-body text-xs">
                          {firstLine(s.highest_risk_areas || s.key_takeaways, 60)}
                        </td>
                        <td className="px-3 py-2.5 text-es-body text-xs">{firstLine(s.actions, 60)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-es-soft bg-es-haze/50 text-sm text-es-body">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of{' '}
                {filtered.length} suppliers
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1 rounded border border-es-hairline bg-es-white disabled:opacity-40 hover:bg-es-haze"
                >
                  Prev
                </button>
                <span>
                  {page} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className="px-3 py-1 rounded border border-es-hairline bg-es-white disabled:opacity-40 hover:bg-es-haze"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <Modal
          isOpen={!!selected}
          onClose={() => {
            setSelected(null)
            setSupplierParam(null)
          }}
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex justify-between items-start gap-3 mb-4">
            <h2 className="text-lg font-bold text-es-navy sr-only">Supplier profile</h2>
            <div className="flex gap-2 ml-auto">
              <EsButton
                type="button"
                variant="secondary"
                onClick={() => toggleCompare(selected.id)}
                className="!px-3 !py-1.5 !text-sm"
              >
                {compareIds.includes(selected.id) ? 'Remove from compare' : 'Add to compare'}
              </EsButton>
              <button
                type="button"
                onClick={() => {
                  setSelected(null)
                  setSupplierParam(null)
                }}
                className="p-1.5 text-es-muted hover:text-es-navy rounded-[var(--radius-sm)] hover:bg-es-haze"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-xl" aria-hidden />
              </button>
            </div>
          </div>
          <SupplierProfile supplier={selected} mode={profileMode} onModeChange={setProfileMode} />
        </Modal>
      )}

      {compareOpen && (
        <Modal
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
          className="max-w-6xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold text-es-navy">
              Compare
              {compareSuppliers.length > 0 && (
                <span className="font-medium text-es-muted text-base ml-2">
                  {compareSuppliers.map((s) => s.name).join(' × ')}
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              <EsButton
                type="button"
                variant="secondary"
                onClick={() => setAddCompareOpen(true)}
                disabled={compareIds.length >= 4}
                className="!px-3 !py-1.5 !text-sm"
              >
                + Add supplier
              </EsButton>
              <button
                type="button"
                onClick={() => setCompareOpen(false)}
                className="p-1.5 text-es-muted hover:bg-es-haze rounded-[var(--radius-sm)]"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-xl" aria-hidden />
              </button>
            </div>
          </div>
          {compareSuppliers.length === 0 ? (
            <p className="text-es-muted text-sm">Select suppliers with the checkboxes in the table.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {compareSuppliers.map((s) => (
                <div key={s.id} className="relative flex-1 min-w-[220px]">
                  <button
                    type="button"
                    onClick={() => toggleCompare(s.id)}
                    className="absolute top-2 right-2 z-10 text-es-muted hover:text-es-navy"
                    aria-label={`Remove ${s.name}`}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden />
                  </button>
                  <CompareCard supplier={s} />
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {addCompareOpen && (
        <Modal isOpen={addCompareOpen} onClose={() => setAddCompareOpen(false)} className="max-w-md max-h-[80vh] overflow-y-auto">
          <h3 className="font-bold text-es-navy mb-3">Add supplier to compare</h3>
          <ul className="space-y-1">
            {base
              .filter((s) => !compareIds.includes(s.id))
              .slice(0, 80)
              .map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-[var(--radius-sm)] hover:bg-es-haze text-sm text-es-body"
                    onClick={() => {
                      toggleCompare(s.id)
                      setAddCompareOpen(false)
                    }}
                  >
                    {s.name}
                    {s.overall_score != null && (
                      <span className="text-es-muted ml-2">{s.overall_score.toFixed(1)}</span>
                    )}
                  </button>
                </li>
              ))}
          </ul>
        </Modal>
      )}
    </div>
  )
}

function CompactRollup({
  title,
  rows,
  alphabetical = false,
  metricMode = 'count',
  denom,
  onRowClick,
}: {
  title: string
  rows: RollupRow[]
  /** When true, show all groups A–Z (scrollable). Otherwise top 8 by high/count. */
  alphabetical?: boolean
  metricMode?: MetricMode
  denom: number
  onRowClick?: (key: string) => void
}) {
  const display = alphabetical ? rows : rows.slice(0, 8)
  const supplierSum = rows.reduce((n, r) => n + r.count, 0)
  const headers = metricMode === 'pct' ? ROLLUP_HEADERS_PCT : ROLLUP_HEADERS_COUNT
  const allRows = rows.map((r) => [
    cell(r.key),
    formatMetric(r.count, denom, metricMode),
    formatMetric(r.high, metricMode === 'pct' ? Math.max(r.count, 1) : denom, metricMode),
  ])
  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h2 className="text-xs font-bold text-es-navy uppercase tracking-wider">{title}</h2>
        <CopyTableButtons
          payload={{ headers: [...headers], rows: allRows }}
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-es-muted">No data.</p>
      ) : (
        <>
          <div className="overflow-x-auto max-h-40 overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 bg-es-white">
                <tr className="text-es-muted text-left border-b border-es-soft">
                  <th className="py-1 font-semibold">Name</th>
                  <th
                    className="py-1 font-semibold text-right"
                    title={metricMode === 'pct' ? '% of filtered set' : 'Count'}
                  >
                    {metricMode === 'pct' ? '%' : '#'}
                  </th>
                  <th
                    className="py-1 font-semibold text-right"
                    title={
                      metricMode === 'pct'
                        ? 'Share of this group that is high risk'
                        : 'High-risk count'
                    }
                  >
                    {metricMode === 'pct' ? 'High %' : 'High'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-es-soft">
                {display.map((r) => (
                  <tr
                    key={r.key}
                    className={onRowClick ? 'cursor-pointer hover:bg-es-haze/60' : undefined}
                    onClick={onRowClick ? () => onRowClick(r.key) : undefined}
                    title={onRowClick ? `Filter to ${r.key}` : undefined}
                  >
                    <td className="py-1 text-es-navy font-medium max-w-[120px] truncate">
                      {onRowClick ? (
                        <span className="text-es-signal">{r.key}</span>
                      ) : (
                        r.key
                      )}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {formatMetric(r.count, denom, metricMode)}
                    </td>
                    <td className="py-1 text-right tabular-nums text-es-riskHigh">
                      {formatMetric(
                        r.high,
                        metricMode === 'pct' ? Math.max(r.count, 1) : denom,
                        metricMode
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-es-muted mt-1.5">
            {alphabetical
              ? `${rows.length} groups · ${supplierSum} suppliers · A–Z`
              : rows.length > 8
                ? `Top 8 of ${rows.length} groups · ${supplierSum} suppliers`
                : `${rows.length} groups · ${supplierSum} suppliers`}
          </p>
        </>
      )}
    </section>
  )
}
